import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { FiX } from 'react-icons/fi';

const CreateEditSupplierModal = ({ mode, initialSupplier, onSuccess, onCancel }) => {
    const isEdit = mode === 'edit';

    const [supplierCode, setSupplierCode] = useState('');
    const [supplierName, setSupplierName] = useState('');
    const [remarks, setRemarks] = useState('');
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [selectedBrandIds, setSelectedBrandIds] = useState([]);

    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState('');

    // Brands sorted alphabetically by Brand name
    const sortedBrands = useMemo(() => {
        return [...brands].sort((a, b) => (a.Brand || '').localeCompare(b.Brand || '', undefined, { sensitivity: 'base' }));
    }, [brands]);

    // Subcategories to show: only from checked categories (deduped, sorted)
    const visibleSubcategories = useMemo(() => {
        if (selectedCategoryIds.length === 0) return [];
        const checkedCats = categories.filter((c) => selectedCategoryIds.includes(c._id));
        const subList = checkedCats.flatMap((c) => c.subcategory || []).filter(Boolean);
        return [...new Set(subList)].sort();
    }, [categories, selectedCategoryIds]);

    // When category is unchecked, remove that category's subcategories from selection (only after options loaded)
    const visibleSubcategoryKey = visibleSubcategories.join(',');
    useEffect(() => {
        if (loadingOptions) return;
        if (visibleSubcategories.length === 0) {
            setSelectedSubcategories([]);
            return;
        }
        setSelectedSubcategories((prev) => prev.filter((s) => visibleSubcategories.includes(s)));
    }, [loadingOptions, visibleSubcategoryKey, visibleSubcategories]);

    // Load options and (for edit) initial values
    useEffect(() => {
        const fetchOptions = async () => {
            setLoadingOptions(true);
            try {
                const token = localStorage.getItem('token');
                const [catRes, brandRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/api/foproducts/categories?page=1&limit=500`, {
                        headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
                    }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/foproducts/brands?page=1&limit=500`, {
                        headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
                    })
                ]);
                if (catRes.data.success) setCategories(catRes.data.data || []);
                if (brandRes.data.success) setBrands(brandRes.data.data || []);
            } catch (err) {
                setError(err.response?.data?.error || err.message);
            } finally {
                setLoadingOptions(false);
            }
        };
        fetchOptions();
    }, []);

    useEffect(() => {
        if (!initialSupplier) {
            setRemarks('');
            return;
        }
        if (!isEdit) return;
        setSupplierCode(initialSupplier.supplierCode || '');
        setSupplierName(initialSupplier.supplierName || '');
        setRemarks(initialSupplier.remarks ?? '');
        const catIds = (initialSupplier.category || []).map((c) => (typeof c === 'object' && c._id ? c._id : c));
        setSelectedCategoryIds(catIds);
        setSelectedSubcategories(Array.isArray(initialSupplier.subcategory) ? [...initialSupplier.subcategory] : []);
        const brandIds = (initialSupplier.brands || []).map((b) => (typeof b === 'object' && b._id ? b._id : b));
        setSelectedBrandIds(brandIds);
    }, [isEdit, initialSupplier]);

    const toggleCategory = (id) => {
        setSelectedCategoryIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const toggleSubcategory = (name) => {
        setSelectedSubcategories((prev) =>
            prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
        );
    };

    const toggleBrand = (id) => {
        setSelectedBrandIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitLoading(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                supplierCode: supplierCode.trim(),
                supplierName: supplierName ? supplierName.trim() : '',
                remarks: remarks != null ? String(remarks).trim() : '',
                category: selectedCategoryIds,
                subcategory: selectedSubcategories.filter((s) => s && String(s).trim()),
                brands: selectedBrandIds
            };
            if (isEdit) {
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/api/foproducts/suppliers/${initialSupplier._id}`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/foproducts/suppliers`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
            }
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const title = isEdit ? 'Edit Supplier' : 'Create New Supplier';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center border-b pb-4 mb-4">
                    <h2 className="text-xl font-bold">{title}</h2>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-700 p-1"
                        aria-label="Close"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    {/* Row 1: Supplier Code, Supplier Name */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Supplier Code *
                            </label>
                            <input
                                type="text"
                                required
                                value={supplierCode}
                                onChange={(e) => setSupplierCode((e.target.value || '').toUpperCase())}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., SUP001"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Supplier Name
                            </label>
                            <input
                                type="text"
                                value={supplierName}
                                onChange={(e) => setSupplierName(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Supplier name (optional)"
                            />
                        </div>
                    </div>

                    {/* Row 2: Category checkboxes, 5 per row */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                        {loadingOptions ? (
                            <p className="text-gray-500 text-sm">Loading...</p>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                                {categories.map((c) => (
                                    <label
                                        key={c._id}
                                        className="flex items-center gap-2 cursor-pointer text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedCategoryIds.includes(c._id)}
                                            onChange={() => toggleCategory(c._id)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-gray-700">{c.category || '(Unnamed)'}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Subcategory checkboxes (only from checked categories), 5 per row */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subcategory</label>
                        {visibleSubcategories.length === 0 ? (
                            <p className="text-gray-500 text-sm">
                                Select at least one category above to see subcategories.
                            </p>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                                {visibleSubcategories.map((name) => (
                                    <label
                                        key={name}
                                        className="flex items-center gap-2 cursor-pointer text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedSubcategories.includes(name)}
                                            onChange={() => toggleSubcategory(name)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-gray-700">{name}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Remarks (optional, allow line breaks) */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                        <textarea
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Optional remarks (line breaks allowed)"
                        />
                    </div>

                    {/* Brands checkboxes, 5 per row */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Brands</label>
                        {loadingOptions ? (
                            <p className="text-gray-500 text-sm">Loading...</p>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                                {sortedBrands.map((b) => (
                                    <label
                                        key={b._id}
                                        className="flex items-center gap-2 cursor-pointer text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedBrandIds.includes(b._id)}
                                            onChange={() => toggleBrand(b._id)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-gray-700">{b.Brand || '(Unnamed)'}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {submitLoading ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

CreateEditSupplierModal.propTypes = {
    mode: PropTypes.oneOf(['create', 'edit']).isRequired,
    initialSupplier: PropTypes.shape({
        _id: PropTypes.string,
        supplierCode: PropTypes.string,
        supplierName: PropTypes.string,
        remarks: PropTypes.string,
        category: PropTypes.array,
        subcategory: PropTypes.arrayOf(PropTypes.string),
        brands: PropTypes.array
    }),
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default CreateEditSupplierModal;
